import { useQuery } from '@tanstack/react-query'
import { configService } from '@/services/configService'

/**
 * Query key for the app config. BOOKING_CONSTRAINTS (constants/booking.js)
 * reads the cached result via queryClient.getQueryData(APP_CONFIG_QUERY_KEY).
 */
export const APP_CONFIG_QUERY_KEY = ['app-config']

const APP_CONFIG_POLL_INTERVAL = 2 * 60 * 1000 // 2 minutes
const APP_CONFIG_RETRY_INTERVAL = 10 * 1000 // while it has never landed

/**
 * Loads /config/app on app load and re-fetches it every 2 minutes so config
 * changes propagate without a page reload. refetchIntervalInBackground keeps
 * polling even while the tab is unfocused.
 */
export function useAppConfig() {
    return useQuery({
        queryKey: APP_CONFIG_QUERY_KEY,
        queryFn: async () => {
            const result = await configService.getAppConfig()
            if (!result.success) throw new Error(result.error)
            return result.data
        },
        refetchInterval: (query) => (
            /* ⚠️ A FAILED CONFIG IS RETRIED IN SECONDS, NOT MINUTES. Two minutes is the
               right cadence for noticing a CHANGED config and far too slow for recovering
               from a missing one: almost everything this app can do is gated on this
               response — the map's token, the airport list, the booking constraints, and
               the Google and Apple sign-in ids. While it is missing, the sign-in buttons
               are disabled with no way for anyone to ask again, which is indistinguishable
               from them being broken, and the only cure anyone finds is reloading. */
            query.state.status === 'error' ? APP_CONFIG_RETRY_INTERVAL : APP_CONFIG_POLL_INTERVAL
        ),
        refetchIntervalInBackground: true,

        /* ⚠️ AND IT KEEPS TRYING RATHER THAN GIVING UP AFTER THREE. The default retry
           budget is meant for a request whose failure a screen can report; this one has no
           screen and no reader. Backing off to fifteen seconds costs nothing and means a
           backend that was restarting when the page loaded does not leave the app crippled
           until someone thinks to reload. */
        retry: true,
        retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 15000),
    })
}

/**
 * THE MAPBOX CREDENTIALS, off the config response the app already holds.
 *
 * ⚠️ FROM THE BACKEND, NEVER FROM `.env` — and this portal has learned that lesson the
 * other way round. Its GOOGLE key is `.env`-only by explicit instruction, with the stated
 * consequence that a key rotated on the backend never reaches the portal. The driver
 * portal's own note records what that cost there: it shipped a Google key with no
 * Directions API enabled while a working one sat unread in the config response.
 *
 * There is no such standing instruction for Mapbox, and no Mapbox variable in any `.env`
 * here, so the map takes the backend's copy. `/passengers/config/app` serves both values
 * under `mapbox` — verified against the dev API, not assumed from the OpenAPI spec, which
 * types the whole `data` object as an untyped array and documents neither.
 *
 * No fallback: `RouteMap` already renders "Map unavailable" when either value is missing,
 * which is a truthful empty state. A hardcoded token would be a stale one.
 */
export function useMapConfig() {
    const {data, isLoading, isError} = useAppConfig()
    return {
        token: data?.mapbox?.access_token || '',
        styleUrl: data?.mapbox?.style_url || '',
        isLoading,
        isError,
    }
}

/**
 * ONE CELL OF `pickup_arrival` — whether the app should watch the pickup point for this
 * party and trip direction, and how wide "at the pickup" is.
 *
 * ⚠️ FROM THE BACKEND, WITH NO FALLBACK NUMBER. The radius and the on/off switch are
 * admin-edited (Settings → Pickup Arrival), so a number written here would silently stop
 * agreeing with the server's own check. A block that has not loaded (or an older backend
 * that does not send it) reads as DISABLED — the geofence stays off rather than guessing.
 *
 * @param {'driver'|'passenger'} role
 * @param {string|null|undefined} direction  `trip_direction`; null / unknown counts as `others`
 * @returns {{enabled: boolean, radiusM: number|null}}
 */
export function usePickupArrivalConfig(role, direction) {
    const {data} = useAppConfig()
    return pickupArrivalCell(data, role, direction)
}

/** The same cell, from an already-loaded config — for picking among several trips. */
export function pickupArrivalCell(config, role, direction) {
    const key = ['from_airport', 'to_airport', 'others'].includes(direction) ? direction : 'others'
    const cell = config?.pickup_arrival?.[role]?.[key]
    const radius = Number(cell?.radius_m)
    return {
        enabled: cell?.enabled === true && Number.isFinite(radius) && radius > 0,
        radiusM: Number.isFinite(radius) && radius > 0 ? radius : null,
    }
}
